import React, { useState } from "react";
import axios from "axios";
import { Lock, Mail, Phone, Inbox, RefreshCw, LogOut, Calendar } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import Logo from "./Logo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminInbox = () => {
  const [password, setPassword] = useState("");
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [enquiries, setEnquiries] = useState([]);

  const fetchEnquiries = async (pwd) => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/enquiries`, {
        password: pwd,
      });
      setEnquiries(res.data);
      setAuthed(true);
    } catch (err) {
      if (err.response && err.response.status === 401) {
        setError("Incorrect password. Please try again.");
      } else {
        setError("Something went wrong. Please try again.");
      }
      setAuthed(false);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (e) => {
    e.preventDefault();
    if (!password) return;
    fetchEnquiries(password);
  };

  const handleLogout = () => {
    setAuthed(false);
    setPassword("");
    setEnquiries([]);
  };

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleString("en-AU", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f6f9f3] px-5">
        <div className="w-full max-w-md">
          <div className="flex justify-center mb-8">
            <Logo />
          </div>
          <div className="bg-white rounded-[2rem] shadow-xl p-8 md:p-10 border border-slate-50">
            <div className="mx-auto h-16 w-16 rounded-2xl bg-[#eef6e8] flex items-center justify-center">
              <Lock className="h-8 w-8 text-[#5aa83a]" />
            </div>
            <h1 className="mt-5 text-2xl font-extrabold text-[#123c74] text-center">
              Admin Inbox
            </h1>
            <p className="mt-2 text-slate-500 text-center text-sm">
              Enter your password to view enquiries.
            </p>
            <form onSubmit={handleLogin} className="mt-6 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="pwd" className="font-semibold text-slate-700">
                  Password
                </Label>
                <Input
                  id="pwd"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  className="h-12 rounded-xl border-slate-200 focus-visible:ring-[#5aa83a]"
                />
              </div>
              {error && (
                <p className="text-sm text-red-600 font-medium">{error}</p>
              )}
              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 rounded-full bg-[#123c74] hover:bg-[#0e2f5c] text-white font-bold disabled:opacity-70"
              >
                {loading ? "Checking..." : "View Enquiries"}
              </Button>
            </form>
          </div>
          <p className="text-center mt-6 text-sm text-slate-400">
            <a href="/" className="hover:text-[#5aa83a]">
              &larr; Back to website
            </a>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f6f9f3]">
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => fetchEnquiries(password)}
              disabled={loading}
              className="rounded-full border-slate-200 gap-2"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              <span className="hidden sm:inline">Refresh</span>
            </Button>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="rounded-full gap-2 text-slate-600"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">Log out</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-11 w-11 rounded-xl bg-[#eef6e8] flex items-center justify-center">
            <Inbox className="h-6 w-6 text-[#5aa83a]" />
          </div>
          <div>
            <h1 className="text-2xl font-extrabold text-[#123c74]">Enquiries</h1>
            <p className="text-slate-500 text-sm">
              {enquiries.length} total &bull; newest first
            </p>
          </div>
        </div>

        {enquiries.length === 0 ? (
          <div className="bg-white rounded-2xl p-14 text-center border border-slate-50">
            <Inbox className="h-12 w-12 text-slate-300 mx-auto" />
            <p className="mt-4 text-slate-500 font-medium">
              No enquiries yet. New submissions will appear here.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {enquiries.map((enq) => (
              <div
                key={enq.id}
                className="bg-white rounded-2xl p-6 border border-slate-50 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h3 className="text-lg font-bold text-[#123c74]">
                      {enq.name}
                    </h3>
                    {enq.service && (
                      <span className="inline-block mt-1 text-xs font-semibold text-[#5aa83a] bg-[#eef6e8] rounded-full px-3 py-1">
                        {enq.service}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 text-sm text-slate-400">
                    <Calendar className="h-4 w-4" />
                    {formatDate(enq.created_at)}
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm">
                  <a
                    href={`mailto:${enq.email}`}
                    className="flex items-center gap-2 text-slate-600 hover:text-[#123c74] font-medium"
                  >
                    <Mail className="h-4 w-4 text-[#5aa83a]" /> {enq.email}
                  </a>
                  <a
                    href={`tel:${enq.phone}`}
                    className="flex items-center gap-2 text-slate-600 hover:text-[#123c74] font-medium"
                  >
                    <Phone className="h-4 w-4 text-[#5aa83a]" /> {enq.phone}
                  </a>
                </div>

                {enq.message && (
                  <p className="mt-4 text-slate-700 bg-[#f6f9f3] rounded-xl p-4 leading-relaxed">
                    {enq.message}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminInbox;
